import type {CreateExtArgs} from '@wix/document-manager-core'
import type {
    CompRef,
    VariantPointer,
    ResolvedRefArray,
    ResolvedVariantRelation,
    InnerElementPath,
    DalValue,
    Pointer,
    ExtractedInnerElementsMap,
    SectionMetadata,
    StyleSchemaEntry,
    CssPropertySchema,
    UnitSize,
    AspectRatioSize
} from '@wix/document-services-types'
import _ from 'lodash'
import {COMP_TYPES, DATA_TYPES, VARIANTABLE_DATA_TYPES} from '../../constants/constants'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import type {BuilderComponentsExtensionAPI} from '../builderComponents/builderComponents'
import type {TPAExtensionAPI} from '../tpa'
import type {PlatformExtensionAPI} from '../platform'
import type {VariablesExtensionAPI} from '../variables/variables'
import {getSemanticType} from '../mobileAlgo/preprocess/conversionDataBuilders'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {ComponentData, ResponsiveSemanticType} from './types'
import type {FiltersAPI} from './filters'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import type {StructureExtensionAPI, SchemaExtensionAPI} from '../..'
import {SECTION_SEMANTIC_TYPES} from './constants'
import type {StylesExtensionApi} from '../style/style'
import type {
    CssCustomPropertyItem,
    CssPropertyItem,
    CustomPropertyEnumOption,
    EditorElement,
    EditorReactComponent
} from '@wix/component-protocol/schema' // eslint-disable-line wix-editor/no-internal-import

export interface DependenciesCollector {
    componentTypes: Set<string>
    globalRefs: {
        styles: Set<string>
        variables: Set<string>
    }
}

export const createDependenciesCollector = (): DependenciesCollector => ({
    componentTypes: new Set(),
    globalRefs: {
        styles: new Set(),
        variables: new Set()
    }
})

export const createExtractors = ({dal, pointers, extensionAPI}: CreateExtArgs, filters: FiltersAPI) => {
    const variantsApi = () => (extensionAPI as VariantsExtensionAPI).variants
    const manifestApi = () => (extensionAPI as ManifestExtensionAPI).manifest
    const innerElementsApi = () => (extensionAPI as InnerElementsExtensionAPI).innerElements
    const builderComponentsApi = () => (extensionAPI as BuilderComponentsExtensionAPI).builderComponents
    const tpaApi = () => (extensionAPI as TPAExtensionAPI).tpa
    const platformApi = () => (extensionAPI as PlatformExtensionAPI).platform
    const variablesApi = () => extensionAPI as VariablesExtensionAPI
    const dataModelApi = () => (extensionAPI as DataModelExtensionAPI).dataModel
    const componentStructureApi = () => (extensionAPI as StructureExtensionAPI).components
    const schemaApi = () => (extensionAPI as SchemaExtensionAPI).schemaAPI
    const stylesApi = () => (extensionAPI as StylesExtensionApi).style

    const CSS_VAR_REGEX = /var\(--([^,)]+)/g

    const processStyleVariableConnections = (
        styleData: Record<string, any>,
        collector: DependenciesCollector
    ): Record<string, any> => {
        const processSubObject = (obj: Record<string, any> | undefined) => {
            if (!obj) return
            delete obj.variableConnections
            for (const value of Object.values(obj)) {
                if (typeof value !== 'string') continue
                for (const [, cssVarName] of value.matchAll(CSS_VAR_REGEX)) {
                    collector.globalRefs.variables.add(cssVarName)
                }
            }
        }

        processSubObject(styleData.cssProperties)
        processSubObject(styleData.cssCustomProperties)

        return styleData
    }

    const resolveSemanticType = (compPointer: CompRef): ResponsiveSemanticType => {
        const componentType = componentStructureApi().getComponentType(compPointer)

        if (componentType === COMP_TYPES.FOOTER_SECTION) {
            return 'FOOTER_SECTION'
        }

        const structureType = componentStructureApi().getType(compPointer)
        const isBuilder = builderComponentsApi().isBuilder(componentType)
        const isTPA = tpaApi().isTpaByCompType(componentType)
        const typeToCheck =
            isBuilder && !isTPA ? manifestApi().getArchType(componentType) ?? componentType : componentType
        return getSemanticType(typeToCheck, structureType, extensionAPI, pointers, compPointer)
    }

    const isAspectRatioSize = (value: any): value is AspectRatioSize => {
        return value.type === 'aspectRatio' && value.value
    }

    const isUnitSize = (value: any): value is UnitSize => {
        return ['px', 'em', 'rem', 'fr', 'percentage', 'vh', 'vw', 'spx'].includes(value.type) && value.value
    }

    const roundDecimalValue = <T extends UnitSize | AspectRatioSize>({type, value}: T): T => {
        const precision = type === 'aspectRatio' ? 2 : 0
        return {type, value: _.round(value, precision)} as T
    }

    // Return undefined to signal "skip this namespace" (will not be written to result)
    type NamespaceExtractorFn = (
        raw: DalValue,
        isBuilder: boolean,
        collector: DependenciesCollector
    ) => DalValue | undefined
    const namespaceExtractors: Record<string, NamespaceExtractorFn> = {
        [DATA_TYPES.layout]: raw => {
            const sanitized = _.omit(deepClone(raw), 'id')
            return _.cloneDeepWith(sanitized, value => {
                if (isUnitSize(value) || isAspectRatioSize(value)) {
                    return roundDecimalValue(value)
                }
            })
        },
        [DATA_TYPES.theme]: (raw, isBuilder, collector) => {
            const itemId = raw.id as string | undefined
            if (itemId && collector && stylesApi().global.isGlobalStyle(itemId)) {
                collector.globalRefs.styles.add(itemId)
                return `#${itemId}`
            }
            if (!isBuilder) return undefined
            const extracted = _.omit(deepClone(raw), 'id')

            processStyleVariableConnections(extracted, collector)
            return extracted
        }
    }

    const defaultNamespaceExtractor: NamespaceExtractorFn = raw => _.omit(deepClone(raw), 'id')

    const getComponentDetailsFromCSM = (componentType: string): any => {
        const builderCompData = platformApi().getBuilderCompAppData(componentType)
        if (!builderCompData) {
            return undefined
        }
        const {appDefinitionId, componentId} = builderCompData
        const appPointer = pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId)
        const appData = dal.get(appPointer)
        const {data} = appData?.components?.find((comp: any) => comp.componentId === componentId) ?? {}
        return data
    }

    const extractInnerElements = (
        compPointer: CompRef,
        collector: DependenciesCollector,
        innerElementPath: InnerElementPath = []
    ): ExtractedInnerElementsMap | undefined => {
        const innerElementsNames = innerElementsApi().getInnerElementsNames(compPointer, innerElementPath)
        if (innerElementsNames.length === 0) {
            return undefined
        }

        const result: ExtractedInnerElementsMap = {}
        for (const name of innerElementsNames) {
            result[name] = {}
            const path = [...innerElementPath, name]

            const data = innerElementsApi().getInnerElementData(compPointer, path)
            const style = innerElementsApi().getInnerElementStyle(compPointer, path)

            if (data) {
                result[name].data = data
            }
            if (style) {
                const extracted = namespaceExtractors[DATA_TYPES.theme](style, true, collector)
                if (extracted !== undefined) {
                    result[name].style = extracted
                }
            }

            const nestedElements = extractInnerElements(compPointer, collector, path)
            if (nestedElements) {
                result[name].innerElements = nestedElements
            }
        }
        return result
    }

    const isRefArray = <T>(data: any): data is ResolvedRefArray<T> => data.type === 'RefArray'
    const isVariantRelation = <T>(data: any): data is ResolvedVariantRelation<T> => data.type === 'VariantRelation'

    const resolveRefArrays = (data: DalValue | undefined, namespace: string, variants?: Pointer[]): any => {
        if (!data) {
            return data
        }

        const refs = schemaApi()
            .extractReferenceFieldsInfo(namespace, data.type, data)
            .filter(ref => ref.isRelationalSplit)

        for (const ref of refs) {
            _.set(data, ref.path, resolveRefArrays(_.get(data, ref.path), ref.referencedMap, variants))
        }

        if (isRefArray(data)) {
            const defaultValue = data.values.find(v => !isVariantRelation(v))
            if (!variants) return defaultValue

            const variantValue = data.values
                .filter(isVariantRelation)
                .find(relation => _.isEqual(new Set(relation.variants), new Set(variants)))

            return variantValue ?? defaultValue
        }

        return data
    }

    const getComponentData = (compPointerWithVariants: CompRef, namespace: string): DalValue | undefined => {
        if (!VARIANTABLE_DATA_TYPES[namespace]) {
            return dataModelApi().components.getItem(compPointerWithVariants, namespace)
        }

        const raw = variantsApi().getComponentItemConsideringVariants(compPointerWithVariants, namespace)
        return resolveRefArrays(raw, namespace, compPointerWithVariants.variants) // resolve RefArrays to their actual values for easier consumption by clients
    }

    const extractData = (
        compPointer: CompRef,
        namespaces: string[],
        collector: DependenciesCollector,
        targetVariants: VariantPointer[] = []
    ): ComponentData => {
        const parentPointer = pointers.structure.getParent(compPointer)
        const children = (componentStructureApi().getChildren(compPointer, false) as CompRef[]) ?? []
        const semanticType = resolveSemanticType(compPointer)
        const result: ComponentData = {
            parent: parentPointer?.id ?? null,
            semanticType,
            components: filters.filterVisibleChildren({children})
        }

        if (SECTION_SEMANTIC_TYPES.has(semanticType)) {
            return result
        }

        const compPointerWithVariants = pointers.components.getComponentVariantsPointer(compPointer, targetVariants)
        const componentType = componentStructureApi().getComponentType(compPointer)
        const isBuilder = componentType && builderComponentsApi().isBuilder(componentType)

        if (componentType && isBuilder) {
            result.componentType = componentType
        }

        for (const namespace of namespaces) {
            const raw = getComponentData(compPointerWithVariants, namespace)
            if (!raw) continue

            const extractor = namespaceExtractors[namespace] ?? defaultNamespaceExtractor
            const extracted = extractor(raw, !!isBuilder, collector)
            if (extracted !== undefined) {
                result[namespace] = extracted
            }
        }

        if (isBuilder) {
            collector.componentTypes.add(componentType)
        }

        const innerElements = extractInnerElements(compPointerWithVariants, collector)
        if (innerElements) {
            result.innerElements = innerElements
        }

        return result
    }

    const extractGlobalVariables = (collector: DependenciesCollector): Record<string, DalValue> => {
        const {global} = variablesApi()
        const mapping: Record<string, DalValue> = {}

        for (const cssVarName of collector.globalRefs.variables) {
            try {
                const variableData = global.variables.getVariableDataByCssVarName(cssVarName)
                if (variableData?.value !== undefined) {
                    mapping[cssVarName] = variableData.value
                }
            } catch {
                // Variable may not exist globally, skip
            }
        }

        return mapping
    }

    const extractGlobalStyles = (collector: DependenciesCollector): Record<string, DalValue> => {
        const result: Record<string, DalValue> = {}
        for (const styleId of collector.globalRefs.styles) {
            const pointer = stylesApi().global.getPointer(styleId)
            const definition = stylesApi().global.definition.get(pointer)
            if (definition) {
                const resolvedStyle = variantsApi().getItemConsideringVariantsForPointer(
                    pointer,
                    DATA_TYPES.theme,
                    'value'
                )
                const resolvedStyleClone = resolvedStyle ? _.omit(deepClone(resolvedStyle), 'id') : undefined
                if (resolvedStyleClone) {
                    processStyleVariableConnections(resolvedStyleClone, collector)
                }
                result[styleId] = {
                    ..._.omit(definition, 'id'),
                    ...(resolvedStyleClone ? {value: resolvedStyleClone} : {})
                }
            }
        }
        return result
    }

    const buildStyleSchemaEntry = (editorElement: EditorElement): StyleSchemaEntry => {
        const buildCssPropertySchema = (
            cssType: string,
            defaultValue?: string,
            options?: CustomPropertyEnumOption[]
        ): CssPropertySchema => ({
            cssType,
            ...(defaultValue ? {default: defaultValue} : {}),
            ...(options ? {values: options.map(o => o.value!)} : {})
        })

        const cssProperties: Record<string, CssPropertySchema> = _.mapValues(
            editorElement?.cssProperties,
            (item: CssPropertyItem, propName: string) => buildCssPropertySchema(propName, item.defaultValue)
        )
        const cssCustomProperties: Record<string, CssPropertySchema> = _.mapValues(
            editorElement?.cssCustomProperties,
            (item: CssCustomPropertyItem) =>
                buildCssPropertySchema(item.cssPropertyType!, item.defaultValue, item.customEnum?.options)
        )

        return {
            cssProperties,
            cssCustomProperties
        }
    }

    const extractSectionMetadata = (collector: DependenciesCollector): SectionMetadata => {
        const result: SectionMetadata = {}
        for (const componentType of collector.componentTypes) {
            const {editorElement} = getComponentDetailsFromCSM(componentType) as EditorReactComponent
            if (!editorElement) continue
            result[componentType] = {[DATA_TYPES.theme]: buildStyleSchemaEntry(editorElement)}
        }
        return result
    }

    return {
        resolveSemanticType,
        getComponentDetailsFromCSM,
        extractInnerElements,
        extractData,
        extractGlobalVariables,
        extractGlobalStyles,
        extractSectionMetadata
    }
}

export type ExtractorsAPI = ReturnType<typeof createExtractors>
